/* eslint-disable max-lines */
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { trackEvent } from '@hakimo-ui/hakimo/util';
import {
  useAlarmGroupPlayback,
  useAlarmGroupStatic,
  useAlarmGroupTime,
} from '../../unified-alarm-group/alarm-provider/contexts';
import {
  AlarmGroupPlaybackContextType,
  AlarmGroupStaticContextType,
  AlarmGroupTimeContextType,
} from '../../unified-alarm-group/types';

dayjs.extend(utc);
dayjs.extend(timezone);

interface RecommendationType {
  type: 'approve' | 'deny' | 'investigate' | 'info';
  label: string;
}

export const REFETCH_INTERVAL = 5000;

export const RECOMMENDATION_TIMESTAMP_REGEX = /\b(\d{1,2}):(\d{2}):(\d{2})\b/gi;

export const RESOLVE_RECOMMENDATION = 'Resolve';

export const getRecommendationType = (
  recommendation: string
): RecommendationType => {
  const lowerCaseRec = recommendation.toLowerCase();
  if (lowerCaseRec.includes('approve') || lowerCaseRec.includes('allow')) {
    return { type: 'approve', label: 'Approve' };
  } else if (lowerCaseRec.includes('deny') || lowerCaseRec.includes('reject')) {
    return { type: 'deny', label: 'Deny' };
  } else if (
    lowerCaseRec.includes('investigate') ||
    lowerCaseRec.includes('review')
  ) {
    return { type: 'investigate', label: 'Investigate' };
  }
  return { type: 'info', label: 'Info' };
};

export const getRecommendationStyles = (recommendation?: string) => {
  const recommendationType = recommendation
    ? getRecommendationType(recommendation)
    : { type: 'info', label: 'Info' };

  switch (recommendationType.type) {
    case 'approve':
      return {
        icon: <CheckCircleIcon className="h-5 w-5 text-emerald-500" />,
        badgeClasses:
          'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
      };
    case 'deny':
      return {
        icon: <ExclamationCircleIcon className="h-5 w-5 text-red-500" />,
        badgeClasses:
          'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
      };
    case 'investigate':
      return {
        icon: <InformationCircleIcon className="h-5 w-5 text-amber-500" />,
        badgeClasses:
          'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
      };
    default:
      return {
        icon: (
          <InformationCircleIcon className="text-primary-500 dark:text-primary-300 h-5 w-5" />
        ),
        badgeClasses:
          'bg-blue-50 text-blue-700 border border-blue-300 dark:border-blue-200 dark:bg-blue-600/30 dark:text-blue-400 dark:border-blue-800',
      };
  }
};

export interface AlarmGroupContextForTimestamp {
  seekTo: AlarmGroupTimeContextType['seekTo'];
  timelineBounds: AlarmGroupPlaybackContextType['timelineBounds'];
  location: AlarmGroupStaticContextType['location'];
  alarmGroupId: AlarmGroupStaticContextType['alarmGroupId'];
}

export const useAlarmGroupContext =
  (): AlarmGroupContextForTimestamp | null => {
    const staticContext = useAlarmGroupStatic();
    const timeContext = useAlarmGroupTime();
    const playbackContext = useAlarmGroupPlayback();

    if (!timeContext?.seekTo) {
      return null;
    }
    return {
      seekTo: timeContext.seekTo,
      timelineBounds: playbackContext.timelineBounds,
      location: staticContext.location,
      alarmGroupId: staticContext.alarmGroupId,
    };
  };

export const createTimestampClickHandler = (
  alarmGroupContext: AlarmGroupContextForTimestamp | null,
  user?: any
) => {
  return (timestampText: string) => {
    if (
      alarmGroupContext?.seekTo &&
      alarmGroupContext?.timelineBounds &&
      alarmGroupContext?.location
    ) {
      const [hours, minutes, seconds] = timestampText.split(':').map(Number);
      const { totalTimelineStartTime, totalTimelineEndTime } =
        alarmGroupContext.timelineBounds;
      // `||`, not `??`: an empty zone reaches here from locations that skip
      // normalizeAlarmGroupLocation's default, and dayjs throws on one.
      const locationTimezone = alarmGroupContext.location?.timezone || 'UTC';

      const timelineStartInTz = dayjs(totalTimelineStartTime).tz(
        locationTimezone
      );
      const targetTimeInTz = timelineStartInTz
        .hour(hours)
        .minute(minutes)
        .second(seconds);
      const targetTimestamp = targetTimeInTz.utc().valueOf();

      if (
        targetTimestamp < totalTimelineStartTime ||
        targetTimestamp > totalTimelineEndTime
      ) {
        trackEvent('ai_recommendation_timestamp_seek_out_of_bounds', {
          timestamp: timestampText,
          target_timestamp: targetTimestamp,
          timeline_start: totalTimelineStartTime,
          timeline_end: totalTimelineEndTime,
          context: 'unified_monitoring',
          out_of_bounds_type:
            targetTimestamp < totalTimelineStartTime
              ? 'before_start'
              : 'after_end',
          alarm_group_id: alarmGroupContext.alarmGroupId,
          location_id: alarmGroupContext.location?.id,
          tenant_id: alarmGroupContext.location?.tenant_id,
          operator_id: user?.id,
        });
        const clampedTimestamp = Math.max(
          totalTimelineStartTime,
          Math.min(targetTimestamp, totalTimelineEndTime)
        );
        alarmGroupContext.seekTo(clampedTimestamp);
      } else {
        alarmGroupContext.seekTo(targetTimestamp);
      }
    }
  };
};
